import { memo, useMemo } from 'react';

import { TiArrowForward } from 'react-icons/ti';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { BiChevronDown, BiChevronUp, BiDotsHorizontalRounded } from 'react-icons/bi';

import { OButton, OPopper } from '@src/shared';
import AvatarCircle from '@src/components/AvatarCircle';
import QuestionStatusLabel from '../QuestionStatusLabel';

import useQuestionBox from './useQuestionBox';

import { EAnswerStatus, EAnswerType, EQuestionStatus, EUserType, IQuestion } from '@src/types';

import styles from './questionBox.module.scss';

interface IQuestionBoxProps {
  question: IQuestion;
  isAnswerSectionOpened: boolean;
  onAnswersToggle: () => void;
  handleOpenAnswerBox: (answerType: EAnswerType) => void;
}

const QuestionBox = ({ question, isAnswerSectionOpened, onAnswersToggle, handleOpenAnswerBox }: IQuestionBoxProps) => {
  const {
    author,
    formattedTime,
    questionToEdit,
    currentPeerType,
    questionActions,
    questionMenuItems,
    isEditableQuestion,
    isActionsButtonEnabled,
    handleMarkAsAnswered,
    isMarkingAnswered,
    showInlineMarkAsAnswered,
  } = useQuestionBox({
    question,
    handleOpenAnswerBox,
  });

  const FooterComponent = useMemo(() => {
    const isAnswered = question.status === EQuestionStatus.CLOSED;
    const isAssigned = question.answer?.status === EAnswerStatus.ASSIGNED;
    const isHost = currentPeerType === EUserType.HOST;
    const isCoHost = currentPeerType === EUserType.CO_HOST;

    if (question.status === EQuestionStatus.DISMISSED) {
      return null;
    }
    if (
      question?.answer?.answerType &&
      question.answer.answerType !== EAnswerType.LIVE &&
      ((isAnswered && currentPeerType === EUserType.HOST) || (!isAnswered && currentPeerType === EUserType.SPEAKER))
    ) {
      return (
        <OButton
          text="Reply by text"
          isTextButton
          startIcon={<TiArrowForward size={16} />}
          onClick={() => question.answer?.answerType && handleOpenAnswerBox(question.answer.answerType)}
        />
      );
    } else if (isActionsButtonEnabled && (isHost || (isCoHost && !isAssigned && !isAnswered))) {
      return (
        <OPopper
          icon={
            <div className={styles.actionsContainer}>
              <TiArrowForward size={16} />
              <span className={styles.actions}>Actions</span>
              <BiChevronDown size={16} />
            </div>
          }
          classes={{ iconButton: styles.menuIcon }}
          position="bottom-start"
          groups={questionActions}
        />
      );
    } else if (showInlineMarkAsAnswered) {
      return (
        <OButton
          text={isMarkingAnswered ? 'Marking…' : 'Mark as answered'}
          isTextButton
          startIcon={<IoIosCheckmarkCircleOutline size={16} className={styles.answeredIcon} />}
          onClick={handleMarkAsAnswered}
          disabled={isMarkingAnswered}
        />
      );
    } else {
      return null;
    }
  }, [
    currentPeerType,
    handleMarkAsAnswered,
    handleOpenAnswerBox,
    isActionsButtonEnabled,
    isMarkingAnswered,
    question.answer?.answerType,
    question.answer?.status,
    question.status,
    questionActions,
    showInlineMarkAsAnswered,
  ]);

  return (
    <div className={styles.questionBox}>
      <AvatarCircle
        peerId={author ? (questionToEdit?.creator.id ?? question.creator.id) : ''}
        name={author ?? 'AN'}
        avatarImgSize={36}
      />

      <div className={styles.questionContent}>
        <div className={styles.questionHeader}>
          <span className={styles.author}>{author ?? 'Anonymous'}</span>
          <div className={styles.timeContainer}>
            <span className={styles.editQuestionLabel}>{question.edited ? '(edited)' : ''}</span>
            <span className={styles.time}>{formattedTime}</span>
          </div>
        </div>

        <div className={`${styles.question} ${questionToEdit?.id === question.id ? styles.questionBoxSelected : ''}`}>
          <div className={styles.questionTextContainer}>
            <div className={styles.questionText}>{question.text}</div>
            {isEditableQuestion && (
              <div className={styles.questionOptions}>
                <OPopper
                  icon={<BiDotsHorizontalRounded size={24} className={styles.menuIcon} />}
                  classes={{ iconButton: styles.menuIcon }}
                  position="bottom-end"
                >
                  <div className={styles.menuWrapper}>
                    {questionMenuItems.map(({ label, onClick }) => (
                      <OButton key={label} text={label} className={styles.menuItem} onClick={onClick} />
                    ))}
                  </div>
                </OPopper>
              </div>
            )}
          </div>
          <div className={styles.questionBottomContainer}>
            <QuestionStatusLabel question={question} />
            {question.answer?.text && (
              <div className={styles.answersDropdown}>
                <OButton
                  size="auto"
                  variant="none"
                  text={`${isAnswerSectionOpened ? 'Hide' : 'Show'} answer`}
                  icon={isAnswerSectionOpened ? <BiChevronUp size={16} /> : <BiChevronDown size={16} />}
                  onClick={onAnswersToggle}
                  classes={{
                    container: styles.answersDropdownButton,
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className={styles.questionFooter}>{FooterComponent}</div>
      </div>
    </div>
  );
};

export default memo(QuestionBox);
